import { defineVersionedStorage } from '@heidi/runtime/storage';
import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

const createSsrSafeStorage = <T>() =>
  createJSONStorage<T>(() => {
    if (typeof window === 'undefined') {
      return {
        getItem: () => null,
        setItem: () => undefined,
        removeItem: () => undefined,
      };
    }
    return window.localStorage;
  });

export const SESSION_PANEL_OPEN_STORAGE_KEY = 'heidi.sessionPageV2.sessionPanelOpen';

/**
 * Whether the V2 session page's right-side metadata panel (Session /
 * Note context / Suggested cards) is visible. Persists across reloads
 * so the user's preference survives navigation between sessions.
 * Defaults to `true` — first-time users see the panel.
 */
export const sessionPanelOpenAtom = atomWithStorage<boolean>(
  SESSION_PANEL_OPEN_STORAGE_KEY,
  true,
  createSsrSafeStorage<boolean>()
);

export const V2_SESSION_PANEL_MIN_WIDTH_PX = 320;
export const V2_SESSION_PANEL_MAX_WIDTH_PX = 448;

// UNCARVED DOMAIN KEY (session-page-v2, SCR-7358): browser preference for the resizable control panel
// eslint-disable-next-line @heidi/rules/no-new-cache-key-declaration -- versioned browser preference, not a query/cache key
export const V2_SESSION_PANEL_WIDTH_STORAGE_KEY = 'heidi.sessionPageV2.panelWidth';

export const sessionPanelWidthStorage = defineVersionedStorage<number>({
  key: V2_SESSION_PANEL_WIDTH_STORAGE_KEY,
  version: 1,
  fallback: V2_SESSION_PANEL_MIN_WIDTH_PX,
  validate: (value): value is number =>
    typeof value === 'number' &&
    value >= V2_SESSION_PANEL_MIN_WIDTH_PX &&
    value <= V2_SESSION_PANEL_MAX_WIDTH_PX,
});

export const sessionPanelWidthAtom = atomWithStorage<number>(
  V2_SESSION_PANEL_WIDTH_STORAGE_KEY,
  V2_SESSION_PANEL_MIN_WIDTH_PX,
  sessionPanelWidthStorage.adapter
);

/**
 * The column is wider when it hosts the AI surface (Work / Ask Heidi) than when
 * it holds the card stack: a chat thread needs the room, and the cards are laid
 * out for 320. The difference is the point — the width change is what makes the
 * switch read as one panel growing into its new contents rather than two panels
 * swapping, so the two widths are deliberately separate preferences rather than
 * one shared number the user has to keep re-dragging.
 */
export const V2_SESSION_PANEL_AI_MIN_WIDTH_PX = 380;
export const V2_SESSION_PANEL_AI_MAX_WIDTH_PX = 720;
const V2_SESSION_PANEL_AI_DEFAULT_WIDTH_PX = 480;

// UNCARVED DOMAIN KEY (session-page-v2, SCR-7358): browser preference for the AI column width
// eslint-disable-next-line @heidi/rules/no-new-cache-key-declaration -- versioned browser preference, not a query/cache key
export const V2_SESSION_PANEL_AI_WIDTH_STORAGE_KEY = 'heidi.sessionPageV2.aiPanelWidth';

export const sessionPanelAiWidthStorage = defineVersionedStorage<number>({
  key: V2_SESSION_PANEL_AI_WIDTH_STORAGE_KEY,
  version: 1,
  fallback: V2_SESSION_PANEL_AI_DEFAULT_WIDTH_PX,
  validate: (value): value is number =>
    typeof value === 'number' &&
    value >= V2_SESSION_PANEL_AI_MIN_WIDTH_PX &&
    value <= V2_SESSION_PANEL_AI_MAX_WIDTH_PX,
});

export const sessionPanelAiWidthAtom = atomWithStorage<number>(
  V2_SESSION_PANEL_AI_WIDTH_STORAGE_KEY,
  V2_SESSION_PANEL_AI_DEFAULT_WIDTH_PX,
  sessionPanelAiWidthStorage.adapter
);

/**
 * Whether the V2 session-panel column is currently the host for the AI surface
 * (Work / Ask Heidi), rather than the page-level `EvidenceSidebarPanel`.
 *
 * Written during render by [[SessionPageV2View]], which is the only place that
 * knows all three inputs — the flag, the V2 layout, and the measured shell
 * width that decides `isCompact`. Read by `useEvidenceSidebarPanel` so exactly
 * one of the two surfaces paints the AI body; without it both would, since the
 * page-level panel sits outside the V2 shell and cannot see its container
 * measurement. The write is during render (not an effect) so the later sibling
 * panel sees the new value in the same commit.
 *
 * In-memory: it tracks live layout, not a preference. `false` whenever the V2
 * view is unmounted, so every non-V2 route keeps the page-level panel.
 */
export const sessionPanelHostsAiSurfaceAtom = atom<boolean>(false);

/**
 * Whether the V2 session panel is rendered as a Sheet (because the shell
 * container is narrower than [[V2_COMPACT_SHELL_WIDTH_PX]]) and currently
 * open. Intentionally in-memory only — the user's persisted wide-mode
 * preference ([[sessionPanelOpenAtom]]) should never auto-open the Sheet
 * on a resize. Defaults to `false` so the Sheet is closed by default
 * whenever the shell crosses into compact mode.
 */
export const sessionPanelSheetOpenAtom = atom<boolean>(false);

/**
 * Evidence sidebar (Ask Heidi) is currently hiding the inline session panel.
 * In-memory, so it never overwrites the persisted [[sessionPanelOpenAtom]]
 * preference. Cleared when Evidence closes and reset on reload. Read/written
 * through [[sessionPanelInlineEffectiveOpenAtom]], not directly.
 */
export const sessionPanelEvidenceCollapsedAtom = atom<boolean>(false);

/**
 * A system-initiated request to reveal the inline session panel for the current
 * session view — e.g. post-note auto-open from Settings → "Automatically open
 * side panel". In-memory only and cleared per session, so it never overwrites
 * the persisted [[sessionPanelOpenAtom]] preference: a user who keeps the
 * inspector closed keeps that preference on the next reload/navigation. ORed
 * into [[sessionPanelInlineEffectiveOpenAtom]]'s read but — exactly like the
 * persisted preference — still suppressed while evidence is collapsing the
 * panel, so auto-open never stacks the two columns. Any manual open/close
 * clears it, so an explicit user choice always wins.
 */
export const sessionPanelAutoOpenAtom = atom<boolean>(false);

/**
 * Whether the inline session panel is visible, combining the saved
 * preference (or a transient auto-open request) with the transient evidence
 * collapse. UI reads and writes this instead of the underlying atoms: reading
 * hides the panel while evidence is up; writing records the user's explicit
 * choice, clears the collapse, and drops any pending auto-open, so a manual
 * open/close always wins.
 */
export const sessionPanelInlineEffectiveOpenAtom = atom(
  (get) =>
    (get(sessionPanelOpenAtom) || get(sessionPanelAutoOpenAtom)) &&
    !get(sessionPanelEvidenceCollapsedAtom),
  (get, set, next: boolean | ((prev: boolean) => boolean)) => {
    const current =
      (get(sessionPanelOpenAtom) || get(sessionPanelAutoOpenAtom)) &&
      !get(sessionPanelEvidenceCollapsedAtom);
    const nextValue = typeof next === 'function' ? next(current) : next;
    set(sessionPanelOpenAtom, nextValue);
    set(sessionPanelEvidenceCollapsedAtom, false);
    set(sessionPanelAutoOpenAtom, false);
  }
);

/**
 * Whether the V2 session layout tour is eligible to enqueue right now.
 *
 * Written by [[SessionPageV2View]]: `true` only while the wide (non-compact,
 * non-mobile) V2 session view is mounted on screen, `false` otherwise. The
 * launch-modal queue reads this so the onboarding tour for the new layout
 * (`ModalId.SCRIBE_LAYOUT_V2_TOUR`) is only queued when the layout it explains
 * is actually visible — and never blocks the queue on routes/sizes where the
 * tour can't run.
 *
 * In-memory only: it tracks live mount state, not a persisted preference.
 */
export const scribeLayoutTourEligibleAtom = atom<boolean>(false);

/**
 * An explicit request to relaunch the V2 layout tour (from the Help menu or a
 * user-triggered V1 → V2 switch). Kept separate from the modal queue until the
 * wide V2 session view mounts, so navigation or a compact layout can never leave
 * an unrenderable user-triggered modal blocking the queue.
 */
export const scribeLayoutTourRelaunchRequestedAtom = atom<boolean>(false);

export type ScribeLayoutInviteVariant = 'labs' | 'ga';

/**
 * Which V2-layout opt-in invite should be queued while the legacy (V1) session
 * view is mounted. `null` means no invite is eligible. The launch-modal queue
 * reads this so `ModalId.SCRIBE_LAYOUT_V2_INVITE` is only queued when the
 * layout it promotes is actually visible — and never blocks elsewhere.
 *
 * In-memory only: persistence remains split between the Labs override (the
 * user's layout choice) and `seen_modals` (whether the GA card was dismissed).
 */
export const scribeLayoutInviteVariantAtom = atom<ScribeLayoutInviteVariant | null>(null);

/**
 * Whether the V2 main canvas is showing the linked patient's profile
 * instead of the active artifact tab. Set `true` by the header
 * patient-link trigger (only meaningful when a patient is linked) and
 * cleared when the user picks any artifact tab, so the profile reads as
 * a transient "tab" that the tab strip itself dismisses.
 *
 * Intentionally in-memory only and reset on session change — the profile
 * is scoped to the current session's linked patient and should never
 * persist across navigation.
 */
export const v2PatientProfileOpenAtom = atom<boolean>(false);

/** Wide single-strip overflow: `true` / `false`, or `null` when no strip is measuring. */
export const v2ArtifactTabStripOverflowingAtom = atom<boolean | null>(null);

/**
 * A V2 session-panel card that can open an inline paginated detail view.
 * The Note context card paginates into its Verify detail view; the Codes
 * card paginates into its add-code / replace-code / code-specification
 * secondary screens; the Tools card paginates into a selected tool (Coding,
 * Calculators). The union is kept as a string literal so additional
 * paginated cards can join without changing the atom shape.
 */
export type V2PaginatedCard = 'note' | 'codes' | 'tools';

/** Every V2 session-panel card that participates in collapse coordination. */
export type V2CardId =
  | 'session'
  | 'tasks'
  | 'send-letter'
  | 'guidance'
  | 'suggested'
  | V2PaginatedCard;

// UNCARVED DOMAIN KEY (session-page-v2, SCR-7374): browser preference shared across control-panel cards
// eslint-disable-next-line @heidi/rules/no-new-cache-key-declaration -- versioned localStorage preference, not a query/cache key
export const V2_CARD_OPEN_PREFERENCES_STORAGE_KEY =
  'heidi.sessionPageV2.cardOpenPreferences';

type V2CardOpenPreferences = Record<string, boolean>;

export const cardOpenPreferencesStorage = defineVersionedStorage<V2CardOpenPreferences>({
  key: V2_CARD_OPEN_PREFERENCES_STORAGE_KEY,
  version: 1,
  fallback: {},
  // Accept unknown card ids so an older desktop binary preserves preferences
  // written by a newer one. Known ids stay type-safe at the hook boundary.
  validate: (value): value is V2CardOpenPreferences =>
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every((open) => typeof open === 'boolean'),
});

/**
 * Last user-selected open state for each control-panel card. Missing entries
 * deliberately fall back in [[useV2CardCollapse]] so new users and newly added
 * cards keep each card's product default.
 */
export const v2CardOpenPreferencesAtom = atomWithStorage<V2CardOpenPreferences>(
  V2_CARD_OPEN_PREFERENCES_STORAGE_KEY,
  {},
  cardOpenPreferencesStorage.adapter,
  { getOnInit: true }
);

/**
 * Which session-panel card (if any) is currently showing a paginated detail
 * view (Note → Verify, Codes → secondary screen). Sibling cards read this
 * via `useV2CardCollapse` to auto-collapse so the active detail view
 * becomes the panel's focus. `null` when every card is at its root view.
 */
export const v2PaginatedCardAtom = atom<V2PaginatedCard | null>(null);

/**
 * One-shot request to force the V2 Codes card open. The floating code-card
 * popover's "Review and edit codes" / "View code specification" CTAs sit
 * inside the note editor — they have no direct handle on the codes card's
 * open state (held locally in `useV2CardCollapse`). They set this atom to
 * `true`; the card consumes the signal on the next render, opens itself, and
 * resets the atom back to `false`.
 *
 * Kept as a request-and-consume signal (rather than the canonical open
 * state) so the existing `useV2CardCollapse` auto-collapse semantics — and
 * the user's own click-to-toggle — stay untouched.
 */
export const v2CodesCardOpenRequestAtom = atom<boolean>(false);

/**
 * One-shot request to *passively* open the V2 Codes view (post-session
 * auto-open from the "default side panel" setting). Distinct from
 * [[v2CodesCardOpenRequestAtom]]: that one is a user drill-in from the note
 * editor and steals focus (publishes `v2PaginatedCardAtom = 'tools'`, which
 * collapses the sibling cards). This one must NOT steal focus — it expands the
 * Codes view and shows its content while leaving every other card exactly as
 * the user had it. The Tools card consumes it, opens into Coding, and skips the
 * paginated-focus publication for this open. In-memory + cleared per session.
 */
export const v2CodesCardAutoOpenRequestAtom = atom<boolean>(false);

/**
 * One-shot request to force the V2 Tasks card open. Consumed by
 * `V2TasksCard` on mount so post-note auto-open can expand the card even
 * though its open state lives in `useV2CardCollapse`.
 */
export const v2TasksCardOpenRequestAtom = atom<boolean>(false);

/**
 * True whenever the post-session default side-panel is anything OTHER than
 * Tasks — i.e. Codes (open Codes only) or "don't auto-open" / CLOSED (open
 * nothing). While set, the Tasks card skips its own routine auto-expand, so the
 * side-panel default fully governs whether Tasks opens itself rather than a
 * routine run forcing it open. Set by `useDefaultSidePanelAutoOpen` from the
 * resolved default; in-memory, tracks the user's setting for the current
 * session view. Does not force the Tasks card closed — only suppresses the
 * automatic expand; the user can still open it by hand.
 *
 * Defaults to `false` so the Tasks card's own curated surfaced-routine
 * auto-expand works out of the box for every user; the hook then flips it to
 * `true` for a non-Tasks default. (An earlier version defaulted `true` to
 * fail-closed on the first-mount ordering gap — the hook effect runs after the
 * card's — but a `true` default suppresses the card's surfaced-routine expand
 * for everyone until the hook resolves, which is not wanted. The residual gap
 * is a single first commit before the hook's suppress effect runs; acceptable
 * given the surfaced model is already curated.)
 *
 * The hook's suppress effect is the SOLE writer and re-derives this from
 * `panelToOpen` on every change (staying `true` while the session query is
 * pending). It is deliberately NOT reset in the session-change effect: that
 * effect runs after the suppress effect, so writing it there would clobber the
 * freshly-derived value for a Tasks default.
 */
export const v2TasksAutoExpandSuppressedAtom = atom<boolean>(false);
